import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import AmountField from '../../../components/AmountField';
// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

// Add getYahooFinanceUrl function
const getYahooFinanceUrl = (symbol) => {
  // Convert symbol to Yahoo Finance format
  let yahooSymbol = symbol;
  if (!symbol.includes('.')) {
    // Check if it's a BSE stock (usually 5 digits)
    if (/^\d{5}$/.test(symbol)) {
      yahooSymbol = `${symbol}.BO`; // .BO for BSE
    } else {
      yahooSymbol = `${symbol}.NS`; // .NS for NSE
    }
  }
  // Proxy through our own /api/yahoo-chart function (Yahoo has no CORS headers)
  const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${yahooSymbol}?interval=1d&range=1d`;
  return `/api/yahoo-chart?url=${encodeURIComponent(yahooUrl)}`;
};

const EquityETF = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-equity-etf', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [fundTransactions, setFundTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editNavValue, setEditNavValue] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');
  
  // Form state
  const [fundName, setFundName] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [navValue, setNavValue] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');
  
  // User's saved fund names (will be populated from existing investments)
  const [userFundNames, setUserFundNames] = useState([]);

  // Add new state variables for live prices
  const [livePrices, setLivePrices] = useState({});
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [priceError, setPriceError] = useState(null);
  const [currentTotalValue, setCurrentTotalValue] = useState(0);

  // Group investments by fund name
  const groupInvestmentsByFund = (investments) => {
    const fundMap = new Map();
    
    investments.forEach(investment => {
      const { name, value, units } = investment;
      
      if (fundMap.has(name)) {
        const fund = fundMap.get(name);
        fund.value += value;
        fund.units += parseFloat(units || 0);
        fund.transactions.push(investment);
      } else {
        fundMap.set(name, {
          name,
          value,
          units: parseFloat(units || 0),
          transactions: [investment]
        });
      }
    });
    
    // Convert map to array
    return Array.from(fundMap.values());
  };

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch equity ETF investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching equity ETF investments for user ID:', user.id);
      
      // Fetch from Supabase
      const { data, error } = await supabase
        .from('equity_etf_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        console.error('Error fetching equity ETF investments:', error);
        throw error;
      }
      
      console.log('Fetched equity ETF data:', data);
      
      // Process data - format and group by fund name
      if (data && data.length > 0) {
        // Extract unique fund names for autocomplete
        const uniqueFundNames = [...new Set(data.map(item => item.name))];
        setUserFundNames(uniqueFundNames);
        
        // Format data for display
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          navValue: item.nav_value,
          purchaseDate: item.purchase_date,
          units: item.units
        }));
        
        // Group investments by fund name
        const groupedInvestments = groupInvestmentsByFund(formattedData);
        setInvestments(groupedInvestments);
        
        // Calculate total value
        const total = groupedInvestments.reduce((sum, fund) => sum + fund.value, 0);
        setTotalValue(total);
        console.log('Total equity ETF value:', total);
      } else {
        console.log('No equity ETF investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserFundNames([]);
      }
    } catch (error) {
      console.error('Error processing equity ETF investments:', error);
      // Fall back to empty state
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Calculate amount when price or units changes
  useEffect(() => {
    if (navValue && units && parseFloat(navValue) > 0 && parseFloat(units) > 0) {
      setAmountInvested((parseFloat(navValue) * parseFloat(units)).toFixed(2));
    } else {
      setAmountInvested('');
    }
  }, [navValue, units]);

  // Calculate amount when price or units changes for edit modal
  useEffect(() => {
    if (editNavValue && editUnits && parseFloat(editNavValue) > 0 && parseFloat(editUnits) > 0) {
      setEditAmountInvested((parseFloat(editNavValue) * parseFloat(editUnits)).toFixed(2));
    } else {
      setEditAmountInvested('');
    }
  }, [editNavValue, editUnits]);
  
  // Load data on component mount
  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  // Add fetchLivePrices function
  const fetchLivePrices = useCallback(async () => {
    setPriceError(null);
    
    try {
      console.log('Starting fetchLivePrices...');

      // Get unique fund names from investments
      const uniqueFunds = [...new Set(investments.map(inv => inv.name))];
      console.log('Unique funds to fetch:', uniqueFunds);

      if (uniqueFunds.length === 0) {
        console.log('No funds to fetch prices for');
        return;
      }

      const newPrices = {};
      let hasValidPrices = false;

      // Process funds in batches to avoid rate limiting
      const batchSize = 2;
      for (let i = 0; i < uniqueFunds.length; i += batchSize) {
        const batch = uniqueFunds.slice(i, i + batchSize);
        console.log(`Processing batch ${i/batchSize + 1}:`, batch);
        
        try {
          // Add a longer delay between batches
          if (i > 0) {
            console.log('Adding delay between batches...');
            await new Promise(resolve => setTimeout(resolve, 3000));
          }

          // Fetch prices for the batch in parallel
          const promises = batch.map(async (fund) => {
            try {
              const url = getYahooFinanceUrl(fund);
              console.log(`Fetching price for ${fund} using URL:`, url);
              
              const response = await axios.get(url, {
                headers: {
                  'Accept': 'application/json'
                },
                timeout: 15000
              });
              
              // Parse the response data if it's a string
              let data;
              try {
                data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
              } catch (parseError) {
                console.error(`Error parsing response for ${fund}:`, parseError);
                return null;
              }

              console.log(`Response for ${fund}:`, data);

              if (data?.chart?.result?.[0]) {
                const result = data.chart.result[0];
                const quote = result.meta;
                
                if (!quote) {
                  console.log(`No quote data for ${fund}`);
                  return null;
                }

                const regularMarketPrice = quote.regularMarketPrice;
                const previousClose = quote.chartPreviousClose;
                
                if (regularMarketPrice && previousClose) {
                  const change = regularMarketPrice - previousClose;
                  const changePercent = (change / previousClose) * 100;

                  return {
                    fund,
                    price: regularMarketPrice,
                    change,
                    changePercent,
                    currency: quote.currency || 'INR',
                    lastUpdated: new Date().toISOString()
                  };
                } else {
                  console.log(`Missing price data for ${fund}:`, { regularMarketPrice, previousClose });
                }
              } else {
                console.log(`Invalid response format for ${fund}:`, data);
              }
              return null;
            } catch (error) {
              console.error(`Error fetching price for ${fund}:`, error.message);
              if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
              }
              return null;
            }
          });

          const results = await Promise.all(promises);
          console.log('Batch results:', results);
          
          results.forEach((result) => {
            if (result) {
              newPrices[result.fund] = {
                price: result.price,
                change: result.change,
                changePercent: result.changePercent,
                currency: result.currency,
                lastUpdated: result.lastUpdated
              };
              hasValidPrices = true;
            }
          });

        } catch (error) {
          console.error(`Error processing batch ${i/batchSize + 1}:`, error);
        }
      }

      if (hasValidPrices) {
        console.log('Successfully fetched prices:', newPrices);
        setLivePrices(newPrices);
        setLastPriceUpdate(new Date());

        // Calculate current total value
        const newTotalValue = investments.reduce((sum, investment) => {
          const livePrice = newPrices[investment.name]?.price;
          if (livePrice) {
            return sum + (investment.units * livePrice);
          }
          return sum + investment.value;
        }, 0);

        setCurrentTotalValue(newTotalValue);
      } else {
        const errorMsg = 'No valid prices could be fetched. Please try again later.';
        console.error(errorMsg);
        setPriceError(errorMsg);
        setCurrentTotalValue(totalValue);
      }

    } catch (error) {
      console.error('Error in fetchLivePrices:', error);
      setPriceError('Failed to fetch prices. Please try again later.');
      setCurrentTotalValue(totalValue);
    }
  }, [investments, totalValue]);

  // Add handleRefreshPrices function
  const handleRefreshPrices = async () => {
    if (livePriceLoading) return; // Prevent multiple simultaneous requests
    
    // Reset states before starting
    setLivePrices({});
    setPriceError(null);
    setLastPriceUpdate(null);
    setLivePriceLoading(true);
    
    try {
      await fetchLivePrices();
    } catch (error) {
      console.error('Error refreshing prices:', error);
      setPriceError('Failed to refresh prices. Please try again.');
    } finally {
      setLivePriceLoading(false);
    }
  };

  // Add PriceLoadingIndicator component
  const PriceLoadingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        Fetching prices...
      </Typography>
    </Box>
  );

  // Update pieChartData to include current values
  const pieChartData = useMemo(() => {
    const currentData = investments.map((investment) => {
      const livePrice = livePrices[investment.name]?.price;
      const currentValue = livePrice 
        ? investment.units * livePrice 
        : investment.value;

      return {
        id: investment.id,
        value: currentValue,
        label: investment.name,
        percentage: ((currentValue / currentTotalValue) * 100).toFixed(1) + '%'
      };
    }).sort((a, b) => b.value - a.value);

    const investedData = investments.map((investment) => ({
      id: investment.id,
      value: investment.value,
      label: investment.name,
      percentage: ((investment.value / totalValue) * 100).toFixed(1) + '%'
    })).sort((a, b) => b.value - a.value);

    return { currentData, investedData };
  }, [investments, livePrices, currentTotalValue, totalValue]);

  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  
  const handleReset = () => {
    setFundName(null);
    setPurchaseDate(new Date());
    setNavValue('');
    setUnits('');
    setAmountInvested('');
  };
  
  // Transaction history modal handlers
  const handleOpenTransactionHistory = (fund) => {
    setSelectedFund(fund);
    setFundTransactions(fund.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedFund(null);
    setFundTransactions([]);
  };
  
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditNavValue(currentTransaction.navValue ? currentTransaction.navValue.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    setEditUnits(currentTransaction.units ? currentTransaction.units.toString() : '');
    
    setOpenEditModal(true);
  };
  
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };
  
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!fundName || !purchaseDate || !navValue || !amountInvested) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the fund name (either from string input or from selected option)
      const fundNameValue = typeof fundName === 'string' ? fundName : fundName.label || fundName;
      
      // Prepare the data for Supabase - only include fields that exist in the schema
      const newInvestment = {
        name: fundNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        nav_value: parseFloat(navValue),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        user_id: user.id // Make sure to include user_id
      };

      console.log('Creating new equity ETF investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('equity_etf_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editNavValue || !editAmountInvested || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        nav_value: parseFloat(editNavValue),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('equity_etf_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedFund && openHistoryModal) {
        // Get the latest transactions for this fund
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('equity_etf_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            navValue: item.nav_value,
            purchaseDate: item.purchase_date,
            units: item.units
          }));
          
          setFundTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('equity_etf_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedFund && openHistoryModal) {
        // Get the latest transactions for this fund
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('equity_etf_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              navValue: item.nav_value,
              purchaseDate: item.purchase_date,
              units: item.units
            }));
            
            setFundTransactions(formattedTransactions);
          } else {
            // No transactions left for this fund, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Add handleSort function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedInvestments = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = (a.value / totalValue) * 100;
        const bAllocation = (b.value / totalValue) * 100;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      } else if (key === 'currentValue') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        return direction === 'asc' 
          ? aCurrentValue - bCurrentValue
          : bCurrentValue - aCurrentValue;
      } else if (key === 'currentAllocation') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aCurrentAllocation = (aCurrentValue / currentTotalValue) * 100;
        const bCurrentAllocation = (bCurrentValue / currentTotalValue) * 100;
        return direction === 'asc' 
          ? aCurrentAllocation - bCurrentAllocation
          : bCurrentAllocation - aCurrentAllocation;
      } else if (key === 'pl') {
        const aCurrentValue = livePrices[a.name]?.price ? a.units * livePrices[a.name].price : a.value;
        const bCurrentValue = livePrices[b.name]?.price ? b.units * livePrices[b.name].price : b.value;
        const aPL = aCurrentValue - a.value;
        const bPL = bCurrentValue - b.value;
        return direction === 'asc' 
          ? aPL - bPL
          : bPL - aPL;
      }
      return 0;
    });

    setInvestments(sortedInvestments);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/equity')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Equity ETFs
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Invested: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </Typography>
              {livePriceLoading ? (
                <PriceLoadingIndicator />
              ) : priceError ? (
                <Chip
                  icon={<ErrorIcon />}
                  label={priceError}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : Object.keys(livePrices).length > 0 ? (
                <>
                  <Typography variant="subtitle1" color="text.secondary">
                    | Current Value: {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      maximumFractionDigits: 0,
                    }).format(currentTotalValue)}
                  </Typography>
                  {lastPriceUpdate && (
                    <Tooltip title={`Last updated: ${lastPriceUpdate.toLocaleTimeString()}`}>
                      <Chip
                        icon={<AccessTimeIcon />}
                        label="Live"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                <Chip
                  icon={<ErrorIcon />}
                  label="No live prices available"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Button
            data-tour="inv-prod-live"
            variant="outlined"
            startIcon={livePriceLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefreshPrices}
            disabled={livePriceLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 140,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {livePriceLoading ? 'Updating...' : 'Get Live Price'}
          </Button>
          
          <Button 
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Equity ETF Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Autocomplete
                value={fundName}
                onChange={(event, newValue) => setFundName(newValue)}
                freeSolo
                autoHighlight
                options={userFundNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userFundNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing funds
                  else {
                    return "Your Fund Names";
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Name of Fund" 
                    required 
                    fullWidth
                    placeholder="Enter or select fund name"
                    helperText="You can type a new fund name or select from your existing funds"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={navValue}
                onChange={(e) => setNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                required
                fullWidth
                type="number"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={amountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Fund: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={editNavValue}
                onChange={(e) => setEditNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={editUnits}
                onChange={(e) => setEditUnits(e.target.value)}
                required
                fullWidth
                type="number"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Fund: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for viewing transaction history */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedFund?.name}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Invested Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedFund?.value || 0)} | 
                Total Units: {(selectedFund?.units || 0).toFixed(2)} | 
                Average Price: ₹{(() => {
                  if (fundTransactions.length === 0) return '0.00';
                  const totalAmount = fundTransactions.reduce((sum, tx) => sum + tx.value, 0);
                  const totalUnits = fundTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                  return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                })()}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseHistoryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    ETF Name
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    Invested Value
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    NAV
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    Units
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundTransactions.length > 0 ? (
                  fundTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      <TableCell>
                        {transaction.name}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.value)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.navValue 
                          ? transaction.navValue.toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.units 
                          ? parseFloat(transaction.units).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {fundTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(fundTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fundTransactions
                        .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseHistoryModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Equity ETFs Found</Typography>
          <Typography variant="body2">
            No equity ETF investments found. Start investing to see them here.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fund Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        ETF Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('value')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Invested Value
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('currentValue')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Current Value
                        {sortConfig.key === 'currentValue' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('pl')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        P/L
                        {sortConfig.key === 'pl' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Invested %
                        {sortConfig.key === 'allocation' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('currentAllocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        Current %
                        {sortConfig.key === 'currentAllocation' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => {
                      const livePrice = livePrices[investment.name];
                      const currentValue = livePrice ? investment.units * livePrice.price : investment.value;
                      const changePercent = livePrice ? livePrice.changePercent : 0;
                      const investedPercentage = ((investment.value / totalValue) * 100).toFixed(1);
                      const currentPercentage = ((currentValue / currentTotalValue) * 100).toFixed(1);
                      const profitLoss = currentValue - investment.value;
                      const profitLossPercent = ((profitLoss / investment.value) * 100).toFixed(1);
                      
                      return (
                        <TableRow
                          key={investment.id}
                          onClick={() => handleOpenTransactionHistory(investment)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: COLORS[index % COLORS.length],
                                  mr: 2,
                                }}
                              />
                              {investment.name}
                              {livePrice && (
                                <Chip
                                  icon={changePercent >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                  label={`₹${livePrice.price.toFixed(2)} (${changePercent.toFixed(2)}%)`}
                                  color={changePercent >= 0 ? 'success' : 'error'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 2 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(investment.value)}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(currentValue)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 500,
                              }}
                            >
                              {profitLoss >= 0 ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(profitLoss))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                }}
                              >
                                {profitLoss >= 0 ? '+' : ''}{profitLossPercent}%
                              </Typography>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {investedPercentage}%
                          </TableCell>
                          <TableCell align="right">
                            {currentPercentage}%
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View transactions">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Total row */}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        '& td': { borderBottom: '2px solid', borderColor: 'divider' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(currentTotalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {(() => {
                          // Show 0 P/L if no live prices are available
                          if (Object.keys(livePrices).length === 0) {
                            return (
                              <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                ₹0
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                  }}
                                >
                                  0.0%
                                </Typography>
                              </Typography>
                            );
                          }

                          const totalPL = currentTotalValue - totalValue;
                          const totalPLPercent = ((totalPL / totalValue) * 100).toFixed(1);
                          const isProfit = totalPL >= 0;
                          
                          return (
                            <Typography
                              sx={{
                                color: isProfit ? 'success.main' : 'error.main',
                                fontWeight: 600,
                              }}
                            >
                              {isProfit ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(totalPL))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isProfit ? 'success.main' : 'error.main',
                                }}
                              >
                                {isProfit ? '+' : ''}{totalPLPercent}%
                              </Typography>
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Grid container spacing={2}>
              {/* Current Value Distribution */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 'var(--fx-card-shadow)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Current Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on live market prices
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    {pieChartData.currentData.length > 0 && (
                      <PieChart colors={chartPalette}
                        series={[
                          {
                            data: pieChartData.currentData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30 },
                          },
                        ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        // slotProps={{
                        //   legend: {
                        //     direction: 'row',
                        //     position: { vertical: 'bottom', horizontal: 'middle' },
                        //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        //     itemMarkWidth: 8,
                        //     itemMarkHeight: 8,
                        //     markGap: 5,
                        //     itemGap: 15,
                        //     sx: {
                        //       display: 'flex',
                        //       flexWrap: 'wrap',
                        //       justifyContent: 'center',
                        //       gap: 2,
                        //     },
                        //   },
                        // }}
                        slotProps={{ legend: { hidden: true } }}
                        tooltip={{
                          trigger: 'item',
                          formatter: (params) => {
                            const value = new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(params.value);
                            return `${params.name}: ${value} (${params.percentage}%)`;
                          },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Invested Value Distribution */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 'var(--fx-card-shadow)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Invested Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on purchase values
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    {pieChartData.investedData.length > 0 && (
                      <PieChart colors={chartPalette}
                        series={[
                          {
                            data: pieChartData.investedData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30 },
                          },
                        ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        // slotProps={{
                        //   legend: {
                        //     direction: 'row',
                        //     position: { vertical: 'bottom', horizontal: 'middle' },
                        //     padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        //     itemMarkWidth: 8,
                        //     itemMarkHeight: 8,
                        //     markGap: 5,
                        //     itemGap: 15,
                        //     sx: {
                        //       display: 'flex',
                        //       flexWrap: 'wrap',
                        //       justifyContent: 'center',
                        //       gap: 2,
                        //     },
                        //   },
                        // }}
                        slotProps={{ legend: { hidden: true } }}
                        tooltip={{
                          trigger: 'item',
                          formatter: (params) => {
                            const value = new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(params.value);
                            return `${params.name}: ${value} (${params.percentage}%)`;
                          },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <GuidedTour steps={TOURS['inv-equity-etf']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default EquityETF;